import { ExternalQuestion, RespondentAnswerTypes } from '@sm/question-widgets/respondent-survey';
import { QuestionTypeVariant, ResponseValue } from '@sm/question-definitions';
import { QuestionAnswers } from '~app/pages/SurveyTaking/types';
import { mapImplicitResponse } from './mapImplicitResponse';

import { GetCurrentSurveySessionQuery, RespApiAnswerInput } from '~lib/generatedGqlTypes';

/** Fills answers and adds "other" value */
const mapMultipleChoiceAnswers = (
  values: ResponseValue<string>[],
  responseBase: Partial<RespApiAnswerInput>
): Partial<RespApiAnswerInput>[] =>
  values.reduce<Partial<RespApiAnswerInput>[]>((acc, { id: answerId, type, value }) => {
    if (answerId) {
      const response: Partial<RespApiAnswerInput> = { ...responseBase, answerId };
      if (type === 'ANSWER' || type === 'COMMENT') {
        response.answerText = value;
      }
      return [...acc, response];
    }
    return acc;
  }, []);

/**
 * Function that maps an individual answer to the shape expected by the API, returned as an array.
 * Reference - expected shapes for each question type:
 * @see https://code.corp.surveymonkey.com/pages/devmonkeys/ResponsesAPI/?shell#the-answer-object
 */
const mapAnswerResponse = (
  { values }: Pick<RespondentAnswerTypes, 'values'>,
  // `RespondentAnswerTypes` does not contain family, variant for presentational or unanswered questions
  { family, variant }: ExternalQuestion,
  responseBase: Partial<RespApiAnswerInput>
): Partial<RespApiAnswerInput>[] => {
  switch (family) {
    // single textbox & comment box
    case 'OPEN_ENDED': {
      switch (variant as QuestionTypeVariant['OPEN_ENDED']) {
        case 'SINGLE_LINE':
        case 'ESSAY': {
          if (!values[0]?.value) {
            return [];
          }
          return [{ ...responseBase, answerText: values[0].value }];
        }
        case 'MANY_FIELDS':
          // eslint-disable-next-line no-console
          console.warn('MANY_FIELDS is not implemented yet');
          return [];
      }
      return []; // needed for no-fallthrough lint rule
    }
    // multiple choice, dropdown, & checkbox (& possibly single/many image choice, may need refinement)
    case 'MULTIPLE_CHOICE': {
      // same logic for all variants:
      // SINGLE_ANSWER_RADIO, SINGLE_ANSWER_MENU, MANY_ANSWERS_CHECKBOX, SINGLE_ANSWER_IMAGE, MANY_ANSWERS_IMAGE;
      return mapMultipleChoiceAnswers(values, responseBase);
    }
    // presentational questions
    case 'PRESENTATION': {
      // same for Image and Text
      return [responseBase];
    }
    default: {
      // keep this warning while we integrate all Question Types
      // eslint-disable-next-line no-console
      console.warn('Question Type not mapped:', family, variant);
      return [];
    }
  }
};

type MapSurveyAnswersArgsType = {
  questions: ExternalQuestion[];
  answers: QuestionAnswers;
  pageId: NonNullable<GetCurrentSurveySessionQuery['surveyTakingSession']['respondent']['currentPage']>['id'];
};

/**
 *
 * @param questions Array containing questions from current page
 * @param answers Object containing answers for the current page with questionId as the key
 * @param pageId Current survey page that user is responding to
 * @returns an array of responses matching the shape expected by mutation, empty responses discarded.
 */
export const mapSurveyAnswersToResponse = ({
  questions,
  answers,
  pageId,
}: MapSurveyAnswersArgsType): Partial<RespApiAnswerInput>[] => {
  return Object.values(answers).reduce<Partial<RespApiAnswerInput>[]>((responses, answer) => {
    /** The base response object containing properties to be included with each response */
    const responseBase: Partial<RespApiAnswerInput> = {
      pageId,
      questionId: answer.questionId,
    };
    // get the current question for the answer by matching questionId from answer
    const currentQuestion = questions.find(q => q.id === answer.questionId);
    if (!currentQuestion) {
      // this should not happen but just to safe log for debugging
      // eslint-disable-next-line no-console
      console.error(`could not find question for Answer`, answer);
      return responses;
    }

    const implicitResponse = mapImplicitResponse(currentQuestion);
    if (implicitResponse) {
      responseBase.randomAssignmentId = implicitResponse;
    }

    return [...responses, ...mapAnswerResponse(answer, currentQuestion, responseBase)];
  }, []);
};

export default mapSurveyAnswersToResponse;
